import React, { useState, useMemo, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../../utils/textUtils';

const CustomerOrderTypeTable = ({ data, totalRecords, isLoading, onPage, lazyParams, onEdit, onDelete, resetPagination = false }) => {
    const { th } = useLanguage();
    const safeParams = lazyParams || { page: 1, rows: 10 };

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const [currentFirst, setCurrentFirst] = useState(0);

    useEffect(() => {
        if (resetPagination) {
            setCurrentFirst(0);
            setSortField(null);
            setSortOrder(null);
        }
    }, [resetPagination]);

    const handlePage = (event) => {
        setCurrentFirst(event.first);
        onPage(event);
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const sortedData = useMemo(() => {
        if (!sortField || !data) return data;

        return [...data].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            let comparison = 0;
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                comparison = aVal.localeCompare(bVal, 'tr', { numeric: true });
            } else if (typeof aVal === 'number' && typeof bVal === 'number') {
                comparison = aVal - bVal;
            } else {
                comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            }

            return sortOrder * comparison;
        });
    }, [data, sortField, sortOrder]);

    const actionTemplate = (row) => (
        <div className="flex gap-2 justify-content-center">
            <i onClick={() => onEdit(row)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('customer.editTooltip')}></i>
            <i onClick={() => onDelete(row)} className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700" style={{ fontSize: '1.1rem' }} title={th('customer.deleteTooltip')}></i>
            <i className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
        </div>
    );

    const usageTypeTemplate = (row) => (row.usageType === 1 ? th('customerOrderType.service') : row.usageType === 2 ? th('customerOrderType.sales') : '');

    const statusTemplate = (row) => <span className={`p-badge p-badge-${row.isActive ? 'success' : 'danger'}`}>{row.isActive ? th('customer.active') : th('customer.inactive')}</span>;

    return (
        <DataTable
            value={sortedData}
            paginator
            lazy
            first={currentFirst}
            rows={safeParams.rows}
            totalRecords={totalRecords}
            loading={isLoading}
            onPage={handlePage}
            onSort={onSort}
            sortField={sortField}
            sortOrder={sortOrder}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
            dataKey="id"
            tableStyle={{ minWidth: '50rem' }}
            showGridlines
            stripedRows
            emptyMessage={th('customerOrderType.emptyMessage')}
            removableSort
            sortMode="single"
        >
            <Column body={actionTemplate} style={{ width: '5%' }} align="center" />
                                    <Column field="dealerName" header={th('customer.dealerName')} sortable body={(rowData) => createTruncatedTextForTable(rowData.dealerName, 40)} />
            <Column field="dealerAddressName" header={th('customer.address')} sortable body={(rowData) => createTruncatedTextForTable(rowData.dealerAddressName, 40)} />
            <Column field="orderTypeName" header={th('customerOrderType.orderType')} sortable />
            <Column field="usageType" header={th('customerOrderType.usageType')} body={usageTypeTemplate} sortable />
            <Column field="contractStartDate" header={th('customer.contractStartDate')} body={(row) => formatDateOnly(row?.contractStartDate)} sortable />
            <Column field="contractEndDate" header={th('customer.contractEndDate')} body={(row) => formatDateOnly(row?.contractEndDate)} sortable />
            <Column field="isActive" header={th('customer.status')} body={statusTemplate} sortable />
        </DataTable>
    );
};

export default CustomerOrderTypeTable;
